import { isDeepLink } from 'owa-url';
import { getOwaWorkload, OwaWorkload } from 'owa-workloads';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import type {
    FullComposeEntrySourceType,
    QuickComposeEntrySourceType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { FullComposeFormViewType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyStartShowCalendarFormPerfDatapoint } from 'owa-calendar-forms-perf';
import { CalendarComponent } from 'owa-calendar-usage-instrumentation';
import { PopoutType } from 'owa-popout-v2';

export const enum OpenCalendarFullComposeActionType {
    Modal,
    DeeplinkPopout,
    ProjectionPopout,
    DeeplinkInSameWindow,
}

export function getOpenCalendarFullComposeActionType(
    viewType?: FullComposeFormViewType
): OpenCalendarFullComposeActionType {
    const isProjectionEnabled = isFeatureEnabled('cal-fullcompose-popout-projection');
    const isModalSupportedInModule = !isDeepLink() && getOwaWorkload() == OwaWorkload.Calendar;
    const shouldForceNewWindow =
        isProjectionEnabled && isHostAppFeatureEnabled('openNewEventInNewWindow');
    const shouldOpenInModal = isModalSupportedInModule && !shouldForceNewWindow;

    // For deeplink->deeplink scenarios, trigger the form to open in the same window
    // with viewType set to Popout
    const shouldOpenInSameDeeplinkWindow =
        isDeepLink() && viewType == FullComposeFormViewType.DeeplinkPopOut;

    if (shouldOpenInSameDeeplinkWindow) {
        return OpenCalendarFullComposeActionType.DeeplinkInSameWindow;
    } else if (shouldOpenInModal) {
        return OpenCalendarFullComposeActionType.Modal;
    } else if (isProjectionEnabled) {
        return OpenCalendarFullComposeActionType.ProjectionPopout;
    } else {
        return OpenCalendarFullComposeActionType.DeeplinkPopout;
    }
}

function shouldStartFullComposePerfDatapoint(
    formOpenActionType: OpenCalendarFullComposeActionType
) {
    // New deeplinks will open in a new window with a new state
    return formOpenActionType != OpenCalendarFullComposeActionType.DeeplinkPopout;
}

export function tryStartFullComposePerfDatapoint(
    formOpenActionType: OpenCalendarFullComposeActionType,
    formId: string,
    bundleAlreadyLoaded: boolean,
    entrySource: FullComposeEntrySourceType
) {
    if (shouldStartFullComposePerfDatapoint(formOpenActionType)) {
        return lazyStartShowCalendarFormPerfDatapoint.importAndExecute(
            'showFullComposePerf',
            formId!,
            CalendarComponent.CalendarFullCompose,
            popoutTypeFromFormOpenActionType(formOpenActionType),
            bundleAlreadyLoaded,
            { entrySource: entrySource }
        );
    }

    return Promise.resolve();
}

export async function startQuickComposePerfDatapoint(
    formId: string,
    component: CalendarComponent,
    bundleAlreadyLoaded: boolean,
    entrySource: QuickComposeEntrySourceType
) {
    return lazyStartShowCalendarFormPerfDatapoint.importAndExecute(
        'showQuickComposePerf',
        formId!,
        component,
        PopoutType.None,
        bundleAlreadyLoaded,
        { entrySource: entrySource }
    );
}

function popoutTypeFromFormOpenActionType(formOpenActionType: OpenCalendarFullComposeActionType) {
    switch (formOpenActionType) {
        case OpenCalendarFullComposeActionType.DeeplinkInSameWindow:
            return PopoutType.Deeplink;
        case OpenCalendarFullComposeActionType.Modal:
            return PopoutType.None;
        case OpenCalendarFullComposeActionType.ProjectionPopout:
            return PopoutType.Projection;
        case OpenCalendarFullComposeActionType.DeeplinkPopout:
            return PopoutType.Deeplink;
    }
}
